/**
 * The class catalogue and the suggestion engine (§4.2).
 *
 * Sportsman classes come in three shapes, and the matcher has to understand all
 * three or it will confidently put a car in the wrong one:
 *
 *   index      Super Comp, Super Gas, Super Street; Quick / Super / Hot Rod.
 *              The class *is* a number — 8.90, 9.90, 10.90. You have to be able
 *              to run it or quicker, and then you drive to the index rather than
 *              to your own dial. So a car SLOWER than the index cannot enter, and
 *              a car far quicker belongs in the class above.
 *   heads-up   Top Sportsman, Top Dragster, Comp, Stock, Super Stock. Entry
 *              turns on what the car *is* — body, engine, factory horsepower
 *              factor — at least as much as on what it runs.
 *   bracket    the Summit ET ladders, and IHRA's Box / No Box. Anything can
 *              enter; you dial your own number. The only real gate is whether
 *              you are allowed a delay box.
 *
 * Drag Week and Sick Week add a fourth shape that breaks the pattern entirely:
 *
 *   drag-and-drive  you drive the car between five tracks on public roads over
 *              five days, and the win goes to the lowest *average* ET of the
 *              week. Almost nothing about entry turns on ET. It turns on what
 *              the car is — tread width, small-block or big-block, power adder
 *              or not, tube chassis or stock frame, minimum weight, body year —
 *              and on the one gate that has no equivalent in sanctioned racing:
 *              the car must be registered, insured, plated and road-legal,
 *              because a trailered race car simply cannot enter.
 *
 *              Where ET appears at all it appears as a *cap* rather than a
 *              window — Street Race is disqualified for running quicker than
 *              8.50, Street Machine Eliminator will not accept a slip under
 *              10.00 — which is why `maxQuicker` and `minSlower` exist below.
 *
 * And a fifth, which is a whole scene rather than a sanctioning body:
 *
 *   radial     X275, Ultra Street, Limited Drag Radial, Radial vs the World.
 *              Heads-up eighth-mile racing where the tire *is* the class — the
 *              rules are written around one tire size, and everything else is
 *              adjusted to keep that field close. Entry turns on the tire, a
 *              stock-frame requirement in the tighter classes, and a per-
 *              combination weight table this page does not reproduce.
 *
 *              These are promoter classes, not a sanctioning body's: X275 at one
 *              race is not identical to X275 at the next, and the rules are
 *              revised mid-season for parity. Every entry says so, because a
 *              racer who turns up on last year's combination is the failure this
 *              catalogue could cause.
 *
 * The catalogue is deliberately the sportsman end of the sport. A racer who
 * needs this tool to find their class is not entering Top Fuel.
 *
 * Nothing here decides anything. A class the engine ranks first is a place to
 * start reading the rulebook — entry is settled by the sanctioning body and the
 * tech inspector, and the page says so beside every result.
 */

import type {
  EngineFamily,
  Frame,
  Interest,
  Tire,
  TireWidth,
  VehicleSpec,
  VehicleType,
} from './spec';
import { eighthToQuarter, isCertified, isPowerAdded, isRearNonStock, tireWidthRank } from './spec';

export type Org = 'NHRA' | 'IHRA' | 'Drag Week' | 'Sick Week' | 'Radial';
export type ClassShape = 'index' | 'heads-up' | 'bracket' | 'drag-and-drive' | 'radial';
export type MatchQuality = 'strong' | 'likely' | 'possible';

/** The drag-and-drive events, which share a gate and a catalogue section. */
export const DRAG_AND_DRIVE: Org[] = ['Drag Week', 'Sick Week'];

export function isDragAndDrive(org: Org): boolean {
  return DRAG_AND_DRIVE.includes(org);
}

/**
 * Which catalogue an org belongs to — the same three names the Catalogue
 * buttons carry, so the filter is a comparison rather than a list of special
 * cases that has to be extended every time an org is added.
 */
export function catalogueOf(org: Org): Exclude<Interest, 'All'> {
  if (isDragAndDrive(org)) return 'Drag Week / Sick Week';
  return org === 'Radial' ? 'Radial classes' : 'NHRA / IHRA';
}

export interface RaceClass {
  key: string;
  org: Org;
  name: string;
  shape: ClassShape;
  blurb: string;
  /** The fixed index, for the classes that run one. */
  index?: number;
  /** The capable quarter-mile ET window the class lives in: [quickest, slowest]. */
  et: [number, number];
  /** Body styles admitted; null means any. */
  bodies: VehicleType[] | null;
  /**
   * Tire constructions admitted, where the class names them. Absent means the
   * class does not care, which is most of sanctioned racing.
   *
   * Radial-versus-slick is its own axis, separate from tread width: X275 admits
   * "a 275/60/15 drag radial *or* a 28x10.6 bias slick", Rowdy Radials "235 or
   * smaller drag radial or 26x8.5 or smaller slick". The two are not the same
   * tire at the same number, which is exactly why the rulebooks give each its
   * own size — so the sizes ride along in `notes` rather than being averaged
   * into one width the racer would then measure against the wrong thing.
   */
  tires?: Tire[];
  /** Minimum race weight, lb. */
  minWeight?: number;
  /** The class will not take an uncertified chassis. */
  needsCert?: boolean;
  /** Delay boxes and throttle stops: banned, or allowed. */
  electronics: 'banned' | 'allowed';
  /** Naturally aspirated only, as a class rule. */
  naOnly?: boolean;
  /** The class exists only for power-adder cars. */
  adderOnly?: boolean;

  // --- drag-and-drive gates ------------------------------------------------
  /** The car has to be road-legal and driven between the tracks. */
  streetLegal?: boolean;
  /** Widest tread the class admits. A narrower tire always satisfies it. */
  maxTire?: TireWidth;
  /** The class is for big-tire cars, so a small tire is what disqualifies you. */
  bigTireOnly?: boolean;
  /** Engine families admitted; absent means any. */
  engineFamilies?: EngineFamily[];
  /** Body model-year window, inclusive. */
  bodyYears?: [number, number];
  /** A slip quicker than this is a disqualification (Street Race: 8.50). */
  maxQuicker?: number;
  /** A slip quicker than this will not be accepted (SME: 10.00). */
  minSlower?: number;
  /** Manual transmission only. */
  manualOnly?: boolean;
  /**
   * The frame the class is written for. Absent means no restriction — Sick
   * Week's Heavy Metal says so in as many words, and Unlimited has none at all.
   */
  frame?: Frame;
  /**
   * The class requires the factory's front geometry: no struts under a car that
   * never had them, no Mustang II crossmember, no straight-axle conversion.
   * Aftermarket arms, coil-overs and K-members in the factory locations are not
   * what this rules out — see FRONT_SUSPENSIONS in spec.ts.
   */
  stockFront?: boolean;
  /**
   * What the class demands of the rear suspension.
   *
   *   'stock'      the factory's concept and pickup points — Street Race, Super
   *                Street, Ultra Street.
   *   'non-stock'  a four-link, ladder bars or a solid-axle conversion is the
   *                *entry requirement*, not a disqualification: Pro Street is
   *                written for cars that have departed from stock.
   *
   * Absent means the class says nothing, which is its own fact worth having —
   * X275 spells out that its tire may be run "on any type of rear suspension".
   */
  rear?: 'stock' | 'non-stock';
  /**
   * An award layered on top of whatever class you actually enter, rather than a
   * class you pick — Drag Week's Stick, Sick Week's Naturally Aspirated. Worth
   * listing because a racer wants to know they are in the running for it, and
   * worth marking because entering it *instead* of a real class is not a thing.
   */
  overlay?: boolean;

  /**
   * Why this class cannot be decided here, when it cannot.
   *
   * Some entries turn on something no form can capture: Gasser admission is
   * "approved subjectively by the tech committee", Beetles requires a Beetle,
   * Sick Week Freaks has body rules of its own. With nothing to gate on, these
   * come back matching everything — a clean "strong match" on a class the engine
   * has not actually checked, which is the same defect as a checklist item
   * marked required with no rule behind it.
   *
   * So they are held out of the ranked list and named separately, with this
   * string as the reason. Listing them at all is still right: a racer wants to
   * know the class exists.
   */
  unjudged?: string;

  /** What to read, and what the engine cannot judge for you. */
  notes: string[];
}

const DOOR_CARS: VehicleType[] = ['Full-bodied car', 'Convertible', 'Truck / SUV'];
const NO_DRAGSTERS: VehicleType[] = ['Full-bodied car', 'Convertible', 'Truck / SUV', 'Roadster', 'Altered'];

/**
 * The ET spread a drag-and-drive field actually covers, quickest to slowest.
 *
 * Not a rule — none of these classes has an ET window. It exists so a pace far
 * outside anything the event sees (a 4-second car, a 25-second car) still falls
 * out of the catalogue rather than matching eighteen classes. The real ET limits
 * are the per-class `maxQuicker` and `minSlower`.
 */
const DND_ET: [number, number] = [5.5, 20.0];

/**
 * What a drag-and-drive entry has to say for itself, given that org, shape,
 * street-legality and the ET spread are the same for every one of them.
 *
 * Writing those four out thirty-six times would be four more chances to typo a
 * gate that decides whether a class appears at all.
 */
type DndEntry = Omit<RaceClass, 'org' | 'shape' | 'et' | 'bodies' | 'electronics' | 'streetLegal'> &
  Partial<Pick<RaceClass, 'bodies' | 'electronics' | 'et'>>;

function dragAndDriveClasses(org: Org, entries: DndEntry[]): RaceClass[] {
  return entries.map((entry) => ({
    bodies: null,
    electronics: 'allowed' as const,
    et: DND_ET,
    ...entry,
    org,
    shape: 'drag-and-drive' as const,
    // Not overridable. Every class in both events requires a registered,
    // insured, plated car that drives the whole route on public roads.
    streetLegal: true,
  }));
}

const dragWeek = (entries: DndEntry[]) => dragAndDriveClasses('Drag Week', entries);
const sickWeek = (entries: DndEntry[]) => dragAndDriveClasses('Sick Week', entries);

/**
 * An eighth-mile pace, in the quarter-mile terms the rest of this file speaks.
 *
 * Every radial class below races the eighth, and every window here is the pace
 * the field actually runs rather than a rule — the same job `DND_ET` does, and
 * held to the same standard: nothing downstream may report it as a class rule.
 */
const radialEt = (quickest: number, slowest: number): [number, number] => [
  eighthToQuarter(quickest),
  eighthToQuarter(slowest),
];

const RADIAL_CLASSES: RaceClass[] = [
  {
    key: 'radial-x275',
    org: 'Radial',
    name: 'X275',
    shape: 'radial',
    blurb: 'Small-tire eighth-mile heads-up, on a 275 radial or a 28x10.6 bias slick, with one restricted power adder.',
    et: radialEt(4.1, 4.8),
    bodies: DOOR_CARS,
    tires: ['DOT drag radial', 'Slick — bias ply'],
    frame: 'Stock frame',
    needsCert: true,
    // "The use of any device (electrical or mechanical) that allows a driver to
    // ascertain the position of their car on the track prohibited."
    electronics: 'banned',
    notes: [
      'The tire is the class: a 275/60/15 or Pro Bracket drag radial, or a 28-inch by 10.6-inch bias slick — your choice.',
      'Stock frame from the front motor plate to the back of the rear wheel tub. Back-halved cars are prohibited, and the wheelbase must stay within an inch of stock.',
      'Any rear suspension is allowed, which is unusual for a class this tight — the rules say so in as many words.',
      'Minimum weight comes from a per-combination table, roughly 2,300–2,550 lb, and the power-adder sizes are capped to match. Read the current table before you build.',
    ],
  },
  {
    key: 'radial-ultra-street',
    org: 'Radial',
    name: 'Ultra Street',
    shape: 'radial',
    blurb: 'American production bodies on a 275 radial, keeping the factory front suspension. One restricted power adder.',
    et: radialEt(4.4, 5.2),
    bodies: DOOR_CARS,
    tires: ['DOT drag radial'],
    frame: 'Stock frame',
    stockFront: true,
    needsCert: true,
    electronics: 'allowed',
    notes: [
      'Factory-type front suspension only — but coil-overs, aftermarket control arms and aftermarket K-members are all allowed, and the strut towers must stay in the factory location.',
      'Stock-type, ladder bar and racing four-link rear suspensions are all permitted. Wheelie bars are not.',
      'Stock frame from the front of the shock tower to the back of the rear wheel tub; back-halved cars are prohibited.',
      'The approved tire list is specific — MT ET Street R and Hoosier DR1 in 275/60-15, with a weight break for the 255 and the 26x8.5. Check it against the current rules.',
    ],
  },
  {
    key: 'radial-ldr',
    org: 'Radial',
    name: 'Limited Drag Radial',
    shape: 'radial',
    blurb: 'The middle ground between X275 and Radial vs the World: a wider radial, and all three power adders.',
    et: radialEt(3.95, 4.45),
    bodies: DOOR_CARS,
    tires: ['DOT drag radial'],
    needsCert: true,
    electronics: 'allowed',
    notes: [
      'Nitrous, turbocharged and supercharged combinations all run here, weighted against each other for parity.',
      'The tire list runs from the 275 Pro Radial up through 315/60, 325/50 and the 295 DOT radials, with the 275 taking a weight break.',
      'There is no single rulebook: LDR is run to the promoter’s rules at each event. Get the current version for the race you are entering.',
    ],
  },
  {
    key: 'radial-rvw',
    org: 'Radial',
    name: 'Radial vs the World',
    shape: 'radial',
    blurb: 'The quickest radial-tire class there is. The tire is the only real restriction; everything else is open.',
    et: radialEt(3.4, 3.9),
    bodies: DOOR_CARS,
    tires: ['DOT drag radial'],
    needsCert: true,
    electronics: 'allowed',
    notes: [
      'Tube-chassis cars on a drag radial, at a pace that puts them among the quickest doorslammers in the sport.',
      'Rules are set by the promoter and vary between events — there is no sanctioning body to appeal to.',
      'Chassis certification, a competition licence and a parachute are all assumed long before this pace.',
    ],
  },
];

export const RACE_CLASSES: RaceClass[] = [
  // --- NHRA -----------------------------------------------------------------
  {
    key: 'nhra-super-comp',
    org: 'NHRA',
    name: 'Super Comp',
    shape: 'index',
    index: 8.9,
    blurb: 'The 8.90 index. Dragsters and door cars together, run on a fixed number.',
    et: [7.5, 8.9],
    bodies: null,
    needsCert: true,
    electronics: 'allowed',
    notes: [
      'You drive to 8.90, not to your own dial — going under is a breakout.',
      'A certified chassis and a competition licence are the price of entry.',
    ],
  },
  {
    key: 'nhra-super-gas',
    org: 'NHRA',
    name: 'Super Gas',
    shape: 'index',
    index: 9.9,
    blurb: 'The 9.90 index, for full-bodied cars, roadsters and altereds — no dragsters.',
    et: [8.5, 9.9],
    bodies: NO_DRAGSTERS,
    minWeight: 2100,
    needsCert: true,
    electronics: 'allowed',
    notes: ['2,100 lb minimum, with the driver.', 'Dragsters run Super Comp instead.'],
  },
  {
    key: 'nhra-super-street',
    org: 'NHRA',
    name: 'Super Street',
    shape: 'index',
    index: 10.9,
    blurb: 'The 10.90 index. Full-bodied cars only, and the heaviest minimum of the three.',
    et: [9.5, 10.9],
    bodies: DOOR_CARS,
    minWeight: 2800,
    electronics: 'allowed',
    notes: ['2,800 lb minimum, with the driver.', 'The usual first step off the bracket ladder into an index class.'],
  },
  {
    key: 'nhra-top-dragster',
    org: 'NHRA',
    name: 'Top Sportsman / Top Dragster',
    shape: 'heads-up',
    blurb: 'Dial-your-own for the quickest sportsman cars — dragsters in one field, door cars in the other.',
    et: [6.0, 7.49],
    bodies: null,
    needsCert: true,
    electronics: 'allowed',
    notes: [
      'Which of the two fields you enter is decided by the body, not the pace.',
      'Full chassis certification, a competition licence and a parachute are all assumed at this end.',
    ],
  },
  {
    key: 'nhra-comp',
    org: 'NHRA',
    name: 'Competition Eliminator',
    shape: 'heads-up',
    blurb: 'Class index racing by weight-per-cubic-inch. The deepest rulebook in sportsman drag racing.',
    et: [6.5, 11.0],
    bodies: null,
    needsCert: true,
    electronics: 'allowed',
    notes: [
      'Your class letter comes out of a weight-per-cubic-inch table — displacement is not optional here.',
      'This engine cannot pick your Comp class. Read the class index and talk to a tech official.',
    ],
  },
  {
    key: 'nhra-super-stock',
    org: 'NHRA',
    name: 'Super Stock',
    shape: 'heads-up',
    blurb: 'Production-bodied cars on a factored horsepower rating, with real modification allowed.',
    et: [8.5, 13.0],
    bodies: ['Full-bodied car', 'Convertible'],
    electronics: 'allowed',
    notes: [
      'Classed off NHRA’s assigned horsepower factor and your weight, not off your timeslip.',
      'The body and drivetrain have to trace back to something the factory built.',
    ],
  },
  {
    key: 'nhra-stock',
    org: 'NHRA',
    name: 'Stock Eliminator',
    shape: 'heads-up',
    blurb: 'The closest thing to a factory car that still races. Tight rules, deep fields.',
    et: [9.5, 15.0],
    bodies: ['Full-bodied car', 'Convertible'],
    naOnly: true,
    electronics: 'allowed',
    notes: [
      'Classed by the Automatic Horsepower Factoring System against your race weight.',
      'Blueprint specs govern the engine; a power adder the factory did not fit takes you out of Stock.',
    ],
  },
  {
    key: 'nhra-et-superpro',
    org: 'NHRA',
    name: 'Summit ET Series — Super Pro',
    shape: 'bracket',
    blurb: 'Dial your own, delay box allowed. Where most quick weekend cars actually race.',
    et: [6.0, 11.99],
    bodies: null,
    electronics: 'allowed',
    notes: ['You set your own dial-in, so nothing about the combination locks you out.'],
  },
  {
    key: 'nhra-et-pro',
    org: 'NHRA',
    name: 'Summit ET Series — Pro',
    shape: 'bracket',
    blurb: 'Footbrake bracket racing: dial your own, no electronics.',
    et: [9.0, 13.99],
    bodies: null,
    electronics: 'banned',
    notes: ['A delay box or throttle stop moves you up to Super Pro.'],
  },
  {
    key: 'nhra-et-sportsman',
    org: 'NHRA',
    name: 'Summit ET Series — Sportsman / Street',
    shape: 'bracket',
    blurb: 'The entry ladder. Street cars, street tires, and no electronics.',
    et: [11.0, 22.0],
    bodies: null,
    electronics: 'banned',
    notes: ['Track-by-track naming and ET cut-offs vary — check the local rules.'],
  },

  // --- IHRA -----------------------------------------------------------------
  {
    key: 'ihra-quick-rod',
    org: 'IHRA',
    name: 'Quick Rod',
    shape: 'index',
    index: 8.9,
    blurb: 'IHRA’s 8.90 index — the quickest of the three Rod classes.',
    et: [7.5, 8.99],
    bodies: null,
    needsCert: true,
    electronics: 'allowed',
    notes: ['IHRA’s equivalent of Super Comp.'],
  },
  {
    key: 'ihra-super-rod',
    org: 'IHRA',
    name: 'Super Rod',
    shape: 'index',
    index: 9.9,
    blurb: 'IHRA’s 9.90 index.',
    et: [8.5, 9.99],
    bodies: null,
    needsCert: true,
    electronics: 'allowed',
    notes: ['IHRA’s equivalent of Super Gas, and it does not exclude dragsters the way Super Gas does.'],
  },
  {
    key: 'ihra-hot-rod',
    org: 'IHRA',
    name: 'Hot Rod',
    shape: 'index',
    index: 10.9,
    blurb: 'IHRA’s 10.90 index.',
    et: [9.5, 10.99],
    bodies: null,
    electronics: 'allowed',
    notes: ['IHRA’s equivalent of Super Street, and more open about what body may run it.'],
  },
  {
    key: 'ihra-top-sportsman',
    org: 'IHRA',
    name: 'Top Sportsman / Top Dragster',
    shape: 'heads-up',
    blurb: 'IHRA’s quickest dial-your-own fields, split by body the same way NHRA’s are.',
    et: [6.0, 7.49],
    bodies: null,
    needsCert: true,
    electronics: 'allowed',
    notes: ['Certification and a competition licence are assumed at this pace.'],
  },
  {
    key: 'ihra-super-stock',
    org: 'IHRA',
    name: 'IHRA Super Stock',
    shape: 'heads-up',
    blurb: 'Production-bodied class racing on IHRA’s own factoring.',
    et: [8.5, 13.0],
    bodies: ['Full-bodied car', 'Convertible'],
    electronics: 'allowed',
    notes: ['IHRA and NHRA factor the same car differently — do not carry a class letter across.'],
  },
  {
    key: 'ihra-stock',
    org: 'IHRA',
    name: 'IHRA Stock',
    shape: 'heads-up',
    blurb: 'IHRA’s factory-bodied class.',
    et: [9.5, 15.0],
    bodies: ['Full-bodied car', 'Convertible'],
    naOnly: true,
    electronics: 'allowed',
    notes: ['Classed against IHRA’s tables, not NHRA’s.'],
  },
  {
    key: 'ihra-box',
    org: 'IHRA',
    name: 'Summit SuperSeries — Box',
    shape: 'bracket',
    blurb: 'IHRA bracket racing with electronics allowed.',
    et: [6.0, 11.99],
    bodies: null,
    electronics: 'allowed',
    notes: ['The IHRA counterpart to Super Pro.'],
  },
  {
    key: 'ihra-no-box',
    org: 'IHRA',
    name: 'Summit SuperSeries — No Box',
    shape: 'bracket',
    blurb: 'IHRA footbrake bracket racing.',
    et: [9.0, 13.99],
    bodies: null,
    electronics: 'banned',
    notes: ['No delay box, no throttle stop.'],
  },

  // --- Hot Rod Drag Week ----------------------------------------------------
  //
  // Transcribed from the official 2025–2027 rules (see sources.ts). One rule
  // governs the whole catalogue and is worth reading before any of the entries:
  // "Vehicles must compete in the lowest class for which they are eligible."
  // So a car matching several of these does not get to choose the softest one —
  // which is why the notes name what pushes a car up rather than down.
  //
  // The ET window on every entry is the practical spread of the event rather
  // than a rule; only `maxQuicker` and `minSlower` are actual limits.
  ...dragWeek([
    {
      key: 'dw-unlimited',
      name: 'Unlimited',
      blurb: "Drag Week's most radical class. No restrictions beyond the rules that bind every entry.",
      notes: ['Chassis must be NHRA certified to 9.99 or quicker.'],
      needsCert: true,
    },
    {
      key: 'dw-ultimate-iron',
      frame: 'Full tube chassis',
      name: 'Ultimate Iron',
      blurb: 'Tube-chassis cars wearing an unaltered steel production body, 1938 and newer.',
      minWeight: 3000,
      needsCert: true,
      notes: [
        'All-steel OE body; only bumpers, hood, deck lid, hatch, fenders and doors may be non-metal.',
        'The body may not be chopped, sectioned, narrowed or stretched.',
        'Chassis must be NHRA certified to 9.99 or quicker.',
      ],
    },
    {
      key: 'dw-pro-street-na',
      rear: 'non-stock',
      name: 'Pro Street — Naturally Aspirated',
      blurb: 'Traditional back-half big-tire cars, with no power adder of any kind.',
      minWeight: 3000,
      needsCert: true,
      naOnly: true,
      bigTireOnly: true,
      notes: ['Tires must be wider than 11.5 in at the tread — that is what makes it Pro Street.'],
    },
    {
      key: 'dw-pro-street-pa',
      rear: 'non-stock',
      name: 'Pro Street — Power Adder',
      blurb: 'Traditional back-half big-tire cars running a blower, turbo or nitrous.',
      minWeight: 3000,
      needsCert: true,
      adderOnly: true,
      bigTireOnly: true,
      notes: ['Tires must be wider than 11.5 in at the tread.'],
    },
    {
      key: 'dw-modified',
      name: 'Modified',
      blurb: 'Power-adder small-tire cars with the most liberal modifications, back-halves included.',
      needsCert: true,
      adderOnly: true,
      maxTire: '11.5 in or smaller — small tire',
      notes: [
        'You must have modifications that keep you OUT of Super Street — Modified is not a softer option.',
        'Chassis must be NHRA certified to 9.99 or quicker.',
      ],
    },
    {
      key: 'dw-super-street-sb',
      frame: 'Stock frame',
      rear: 'stock',
      name: 'Super Street — Small-Block',
      blurb: 'Small-tire cars keeping a stock-concept rear suspension. 430 ci and under.',
      minWeight: 3000,
      needsCert: true,
      maxTire: '11.5 in or smaller — small tire',
      engineFamilies: ['Small-block — 430 ci and under'],
      notes: [
        'You must have modifications that keep you OUT of Street Race — like Modified above it, this is not a softer option.',
        'Billet blocks and heads prohibited; the block must be an OE architecture.',
      ],
    },
    {
      key: 'dw-super-street-bb',
      frame: 'Stock frame',
      rear: 'stock',
      name: 'Super Street — Big-Block',
      blurb: 'Small-tire cars keeping a stock-concept rear suspension. 431 ci and up.',
      minWeight: 3000,
      needsCert: true,
      maxTire: '11.5 in or smaller — small tire',
      engineFamilies: ['Big-block — 431 ci up, or big-block architecture'],
      notes: [
        'You must have modifications that keep you OUT of Street Race — like Modified above it, this is not a softer option.',
        'Billet blocks and heads prohibited; the block must be an OE architecture.',
      ],
    },
    {
      key: 'dw-all-motor-sb',
      name: 'All Motor — Small-Block',
      blurb: 'Naturally aspirated small-tire cars with an open rear suspension design.',
      naOnly: true,
      maxTire: '11.5 in or smaller — small tire',
      engineFamilies: ['Small-block — 430 ci and under'],
      notes: [
        'Chassis must be NHRA certified to 11.49 or quicker — the one category that is not a 9.99 tag.',
        'You must have modifications that keep you out of Street Race.',
      ],
    },
    {
      key: 'dw-all-motor-bb',
      name: 'All Motor — Big-Block',
      blurb: 'Naturally aspirated small-tire cars with an open rear suspension design.',
      naOnly: true,
      maxTire: '11.5 in or smaller — small tire',
      engineFamilies: ['Big-block — 431 ci up, or big-block architecture'],
      notes: ['Chassis must be NHRA certified to 11.49 or quicker.'],
    },
    {
      key: 'dw-street-race-sb-na',
      frame: 'Stock frame',
      rear: 'stock',
      stockFront: true,
      name: 'Street Race — Small-Block NA',
      blurb: "Drag Week's most restricted class: OE body and frame, bolt-on parts.",
      minWeight: 3200,
      naOnly: true,
      maxTire: '11.5 in or smaller — small tire',
      engineFamilies: ['Small-block — 430 ci and under'],
      maxQuicker: 8.5,
      notes: ['Lenco-type transmissions prohibited.'],
    },
    {
      key: 'dw-street-race-bb-na',
      frame: 'Stock frame',
      rear: 'stock',
      stockFront: true,
      name: 'Street Race — Big-Block NA',
      blurb: 'OE body and frame, bolt-on performance parts, no power adder.',
      minWeight: 3200,
      naOnly: true,
      maxTire: '11.5 in or smaller — small tire',
      engineFamilies: ['Big-block — 431 ci up, or big-block architecture'],
      maxQuicker: 8.5,
      notes: ['Lenco-type transmissions prohibited.'],
    },
    {
      key: 'dw-street-race-sb-pa',
      frame: 'Stock frame',
      rear: 'stock',
      stockFront: true,
      name: 'Street Race — Small-Block Power-Adder',
      blurb: 'OE body and frame, bolt-on parts, blown or nitrous small-block.',
      minWeight: 3200,
      adderOnly: true,
      maxTire: '11.5 in or smaller — small tire',
      engineFamilies: ['Small-block — 430 ci and under'],
      maxQuicker: 8.5,
      notes: ['Lenco-type transmissions prohibited.'],
    },
    {
      key: 'dw-street-race-bb-pa',
      frame: 'Stock frame',
      rear: 'stock',
      stockFront: true,
      name: 'Street Race — Big-Block Power-Adder',
      blurb: 'OE body and frame, bolt-on parts, blown or nitrous big-block.',
      minWeight: 3200,
      adderOnly: true,
      maxTire: '11.5 in or smaller — small tire',
      engineFamilies: ['Big-block — 431 ci up, or big-block architecture'],
      maxQuicker: 8.5,
      notes: ['Lenco-type transmissions prohibited.'],
    },
    {
      key: 'dw-a-gas',
      unjudged: 'admission is approved subjectively by the Drag Week tech committee',
      name: 'A/Gas',
      blurb: "The radical, supercharged half of the Gasser category — '60s-style doorslammers.",
      bodyYears: [1928, 1968],
      maxTire: '11.5 in or smaller — small tire',
      bodies: ['Full-bodied car', 'Truck / SUV'],
      notes: [
        'The only power adder allowed is a non-screw roots blower up to 8-71. No turbos, no nitrous.',
        'Appearance is judged subjectively by the tech committee — get photos approved before you enter.',
        "Engines must be from '68-and-earlier American families. Wheels no more than 16 in.",
      ],
    },
    {
      key: 'dw-b-gas',
      unjudged: 'admission is approved subjectively by the Drag Week tech committee',
      name: 'B/Gas',
      blurb: 'The restricted, naturally aspirated half of the Gasser category.',
      bodyYears: [1928, 1968],
      maxTire: '11.5 in or smaller — small tire',
      bodies: ['Full-bodied car', 'Truck / SUV'],
      naOnly: true,
      notes: [
        'Appearance is judged subjectively by the tech committee — get photos approved before you enter.',
        'No convertibles or roadsters. Wheels no more than 16 in.',
      ],
    },
    {
      key: 'dw-hot-rod',
      name: 'Hot Rod',
      blurb: "'28 to '48 American cars and trucks, in a traditional rather than Gasser style.",
      bodyYears: [1928, 1948],
      notes: [
        'Look like a Gasser and you will be moved to the Gasser category.',
        'No round-tube framerails; the frame must be original or a street-oriented replacement.',
      ],
    },
    {
      key: 'dw-street-machine-eliminator',
      name: 'Street Machine Eliminator',
      blurb: 'An Open Comp bracket race off your five-day average, on the final day.',
      minSlower: 10.0,
      electronics: 'banned',
      notes: [
        'Cars quicker than 10.00 are not allowed, and a slip under 10.00 will not be accepted.',
        'Your dial-in is the five-day average minus a tenth, and it cannot change between rounds.',
        'No delay boxes, throttle stops, crossover boxes or stepper throttles.',
      ],
    },
    {
      key: 'dw-stick',
      name: 'Stick',
      blurb: 'A heads-up shootout on the last day for every manual-transmission car in the event.',
      manualOnly: true,
      overlay: true,
      notes: [
        'You still enter whichever class you are eligible for, and can win that too.',
        'Pedal-operated clutch, mechanical lever, no air shifters, no torque converters, no Lenco.',
      ],
    },
  ]),

  // --- Sick Week ------------------------------------------------------------
  //
  // Transcribed from the official 2026 rulebook, rev. 2025-5 (see sources.ts).
  // Same drag-and-drive shape as Drag Week and largely the same vocabulary, but
  // the two are NOT interchangeable: Sick Week adds Heavy Metal and the 275
  // classes, splits its tire rules finer, and sets its fluid-containment rule at
  // a different ET. Copying a class name across is how a racer gets it wrong.
  ...sickWeek([
    {
      key: 'sw-unlimited',
      name: 'Unlimited',
      blurb: 'The most radical class, with no restrictions beyond the rules binding every entry.',
      notes: ['No class awards — this one is raced for the overall win.'],
    },
    {
      key: 'sw-unlimited-iron',
      frame: 'Full tube chassis',
      name: 'Unlimited Iron',
      blurb: 'Tube-chassis cars under an unaltered production body, 1938 and newer.',
      minWeight: 3000,
      notes: [
        'The body must be an OE production shell with at least 5,000 sold to the public.',
        'No chopping, sectioning, narrowing, pinching or stretching.',
      ],
    },
    {
      key: 'sw-heavy-metal',
      name: 'Heavy Metal',
      blurb: 'Stock-body cars that make the weight. No chassis restrictions and no tire restrictions.',
      minWeight: 3550,
      notes: ['The heaviest minimum in the event, and the freest rules in exchange.'],
    },
    {
      key: 'sw-modified',
      name: 'Modified',
      blurb: 'Small-tire cars with the most liberal level of modifications.',
      maxTire: '11.5 in or smaller — small tire',
      notes: ['Tires no greater than 315/60-15 or 11.50 in at the tread.', 'Stock-appearing body required.'],
    },
    {
      key: 'sw-pro-street',
      rear: 'non-stock',
      name: 'Pro Street',
      blurb: 'Traditional back-half big-tire cars with a non-stock rear suspension.',
      minWeight: 3000,
      bigTireOnly: true,
      notes: ['Tires must be wider than 11.5 in at the tread.'],
    },
    {
      key: 'sw-super-street',
      frame: 'Stock frame',
      rear: 'stock',
      name: 'Super Street',
      blurb: 'Small-tire cars with fewer restrictions than Street Race, keeping stock-concept rear suspension.',
      minWeight: 3000,
      needsCert: true,
      maxTire: '11.5 in or smaller — small tire',
      notes: [
        'Chassis must be NHRA certified to 9.99 or quicker.',
        'You must have modifications that keep you out of Street Race.',
      ],
    },
    {
      key: 'sw-275-street-race',
      frame: 'Stock frame',
      rear: 'stock',
      stockFront: true,
      name: '275 Street Race',
      blurb: 'OE bodies and frames on a 275 tire, with bolt-on performance parts.',
      minWeight: 2800,
      maxTire: '275 or smaller',
      maxQuicker: 8.5,
      notes: ['Tires no greater than 275/60-15 at the tread.'],
    },
    {
      key: 'sw-sick-street-race',
      frame: 'Stock frame',
      rear: 'stock',
      stockFront: true,
      name: 'Sick Street Race',
      blurb: 'The heavier street-race class, on OE bodies and frames with bolt-on parts.',
      minWeight: 3200,
      maxTire: '11.5 in or smaller — small tire',
      maxQuicker: 8.5,
      notes: ['Post a slip quicker than 8.50 at any point and you are subject to disqualification.'],
    },
    {
      key: 'sw-stick-shift',
      name: 'Stick Shift',
      blurb: 'Clutch-shifted cars, with a weight minimum only for power-adder V8s.',
      manualOnly: true,
      notes: [
        'Power-adder V8s must make 3,000 lb; naturally aspirated V8s and non-V8s have no minimum.',
        'Clutch-assisted shift required. No Bruno or Lenco.',
      ],
    },
    {
      key: 'sw-rowdy-radials',
      name: 'Rowdy Radials',
      blurb: 'The narrowest tire in the event, raced over the eighth rather than the quarter.',
      maxTire: '235 or smaller',
      notes: ['235 or smaller drag radial, or a 26x8.5 or smaller slick.', 'AWD cars may run any street radial.'],
    },
    {
      key: 'sw-freaks',
      unjudged: 'the body rules are specific to the class and are not asked for here',
      name: 'Sick Week Freaks',
      blurb: 'The oddballs — production bodies that do not fit anywhere else.',
      notes: ['Body must be an OE production vehicle with at least 5,000 sold. Check the rulebook for the rest.'],
    },
    {
      key: 'sw-a-gas',
      unjudged: 'admission is approved subjectively by the Sick Week tech committee',
      name: 'A/Gas',
      blurb: "The supercharged half of the Gasser category — '60s-style doorslammers.",
      bodyYears: [1928, 1968],
      maxTire: '11.5 in or smaller — small tire',
      bodies: ['Full-bodied car', 'Truck / SUV'],
      notes: ['Appearance is judged subjectively — get it approved before you enter.'],
    },
    {
      key: 'sw-b-gas',
      unjudged: 'admission is approved subjectively by the Sick Week tech committee',
      name: 'B/Gas',
      blurb: 'The naturally aspirated half of the Gasser category.',
      bodyYears: [1928, 1968],
      maxTire: '11.5 in or smaller — small tire',
      bodies: ['Full-bodied car', 'Truck / SUV'],
      naOnly: true,
      notes: ['Appearance is judged subjectively — get it approved before you enter.'],
    },
    {
      key: 'sw-hot-rod',
      name: 'Hot Rods',
      blurb: "'28 to '48 American cars and trucks.",
      bodyYears: [1928, 1948],
      notes: ['Appear in the Gasser style and you will be moved to that category.'],
    },
    {
      key: 'sw-beetles',
      unjudged: 'it has to be a Beetle, which this form has no way to ask',
      name: 'Beetles',
      blurb: 'Exactly what it says. Sick Week runs a Beetle class.',
      notes: ['See the rulebook for the engine and chassis rules — they are specific to the car.'],
    },
    {
      key: 'sw-pro-dyo',
      name: 'Pro DYO',
      blurb: 'Dial-your-own for quick cars, won on consistency rather than speed.',
      minSlower: 8.5,
      notes: [
        'Hand in one slip per day between 8.500 and 9.999, and average 9.999 or below.',
        'Chassis must be teched quicker than 10.00.',
        'The win goes to the smallest spread across the week, not the quickest average.',
      ],
    },
    {
      key: 'sw-dyo',
      name: 'DYO',
      blurb: 'Dial-your-own for everyone else, won on consistency.',
      minSlower: 10.0,
      notes: ['Hand in one slip per day at 10.000 or above.', 'Smallest spread across the week wins.'],
    },
    {
      key: 'sw-naturally-aspirated',
      name: 'Naturally Aspirated',
      blurb: 'An award across the event for the quickest car running no power adder.',
      naOnly: true,
      overlay: true,
      notes: ['You must still meet the rules for Modified, Pro Street or a Street Race class.'],
    },
    {
      key: 'sw-naturally-aspirated-sb',
      name: 'Naturally Aspirated Small-Block',
      blurb: 'The same award, narrowed to 430 ci and under.',
      naOnly: true,
      overlay: true,
      engineFamilies: ['Small-block — 430 ci and under'],
      notes: ['You must still meet the rules for Heavy Metal, Modified, Pro Street, Super Street or Street Race.'],
    },
  ]),

  // --- radial-tire heads-up racing ------------------------------------------
  //
  // A scene rather than a sanctioning body, and the catalogue has to be honest
  // about what that means: there is no single rulebook. X275 and Ultra Street
  // below are transcribed from the published class rules (see sources.ts);
  // Limited Drag Radial and Radial vs the World are run to the promoter's rules
  // at each event, and the entries say so rather than implying a document that
  // does not exist.
  //
  // All of it is eighth-mile racing. The ET windows here are quarter-mile
  // equivalents — `eighthToQuarter` of the pace each field actually runs —
  // because that is the axis every other class in this file is measured on, and
  // two units in one column would be worse than a conversion. The page prints
  // both numbers beside each other anyway.
  ...RADIAL_CLASSES,
];

export interface ClassMatch {
  cls: RaceClass;
  quality: MatchQuality;
  /** Why it fits, in the racer's own numbers. */
  fits: string[];
  /** What would have to change before entry is possible. */
  blockers: string[];
}

/** How far outside a class's ET window a car can sit and still be worth listing. */
const ET_SLACK = 0.75;

const lb = (n: number) => n.toLocaleString('en-US') + ' lb';

/**
 * The spec and its resolved pace → the classes worth reading about, best first.
 *
 * Two kinds of mismatch, kept apart on purpose:
 *
 *   dropped   the class is not a candidate at all. A dragster is not going to be
 *             let into Super Gas, and listing it as "possible" is noise.
 *   blocker   the class is the right shape for this car, but something concrete
 *             is in the way — 400 lb of minimum weight, an uncertified cage.
 *             Those are worth saying out loud, because they are a to-do list.
 */
export function matchClasses(spec: VehicleSpec, et: number): ClassMatch[] {
  const matches: ClassMatch[] = [];

  for (const cls of RACE_CLASSES) {
    // --- catalogue selection -----------------------------------------------
    //
    // Sanctioned, drag-and-drive and radial answer different questions. Showing
    // all of them at once buried the answer under a screenful of cards, so the
    // racer picks — from the buttons, or from the URL they arrived on.
    if (spec.interest !== 'All' && catalogueOf(cls.org) !== spec.interest) continue;

    // --- gates -------------------------------------------------------------
    if (cls.bodies && !cls.bodies.includes(spec.vehicleType)) continue;

    /**
     * A trailered car is not a candidate for a drag-and-drive class — it is not
     * a to-do item either, because "make the car road-legal" is the whole
     * premise of the event rather than a box to tick. Gate, not blocker.
     */
    if (cls.streetLegal && !spec.streetLegal) continue;

    /**
     * Body year is the same kind of fact: a 1969 Camaro is never going to be a
     * '28–'48 Hot Rod. Only gate when the year is actually known — an unstated
     * year leaves the class listed, with a note to check it.
     */
    if (cls.bodyYears && spec.bodyYear !== null) {
      if (spec.bodyYear < cls.bodyYears[0] || spec.bodyYear > cls.bodyYears[1]) continue;
    }

    const [quickest, slowest] = cls.et;
    if (et < quickest - ET_SLACK || et > slowest + ET_SLACK) continue;

    /**
     * Too slow for an index is a gate, not a blocker.
     *
     * Every other shortfall on this list is a to-do — buy 400 lb of ballast,
     * get the cage tagged, unplug the box. Being unable to run the number is
     * not: you cannot add your way to quicker, and an index class you cannot
     * reach is simply not your class. Listing Super Comp as "possible, with
     * changes" for a 9.60 car would be a suggestion with nothing behind it.
     */
    if (cls.index !== undefined && et > cls.index) continue;

    const inWindow = et >= quickest && et <= slowest;

    // --- reasons and blockers ---------------------------------------------
    const fits: string[] = [];
    const blockers: string[] = [];

    if (cls.index !== undefined) {
      fits.push(`Your ${et.toFixed(2)} pace can run the ${cls.index.toFixed(2)} index.`);
    } else if (cls.shape === 'drag-and-drive') {
      /**
       * Say nothing about the ET window.
       *
       * `DND_ET` is a sanity bound on the catalogue, not a rule — no
       * drag-and-drive class has an ET window at all. Reporting "9.00 sits
       * inside the class's usual 5.50–20.00 window" as a reason the class fits
       * dresses an implementation detail up as a rulebook fact, which is
       * exactly the kind of thing a racer would repeat to a tech official.
       * The real ET limits are `maxQuicker` and `minSlower`, handled below.
       */
    } else if (inWindow) {
      fits.push(`${et.toFixed(2)} sits inside the class's usual ${quickest.toFixed(2)}–${slowest.toFixed(2)} window.`);
    } else {
      blockers.push(
        `${et.toFixed(2)} is just outside the class's usual ${quickest.toFixed(2)}–${slowest.toFixed(2)} window.`,
      );
    }

    if (cls.bodies) fits.push(`A ${spec.vehicleType.toLowerCase()} is an admitted body style.`);

    if (cls.minWeight !== undefined) {
      if (spec.weight === null) {
        blockers.push(`Minimum race weight is ${lb(cls.minWeight)} — enter yours to check it.`);
      } else if (spec.weight < cls.minWeight) {
        blockers.push(`Minimum race weight is ${lb(cls.minWeight)}; you are at ${lb(spec.weight)}.`);
      } else {
        fits.push(`Over the ${lb(cls.minWeight)} minimum, at ${lb(spec.weight)}.`);
      }
    }

    if (cls.needsCert) {
      if (isCertified(spec.chassis)) {
        fits.push('Your chassis already carries a certification tag.');
      } else {
        blockers.push(`Needs a certified chassis — yours is "${spec.chassis}".`);
      }
    }

    if (cls.electronics === 'banned' && spec.electronics) {
      blockers.push('No delay box or throttle stop in this class.');
    } else if (cls.electronics === 'banned') {
      fits.push('No electronics allowed here, and you are running none.');
    }

    if (cls.naOnly && isPowerAdded(spec.powerAdder)) {
      blockers.push(
        isDragAndDrive(cls.org)
          ? `This is a naturally aspirated class; you are running ${spec.powerAdder.toLowerCase()}.`
          : `${spec.powerAdder} takes you out of this class unless the factory fitted it.`,
      );
    }

    if (cls.adderOnly && !isPowerAdded(spec.powerAdder)) {
      blockers.push('This class is for power-adder cars — a blower, turbo or nitrous is required.');
    }

    // --- tread width -------------------------------------------------------
    //
    // A ceiling, so anything narrower passes. Stated as a blocker rather than a
    // gate because tires are the cheapest thing on this list to change.
    if (cls.maxTire) {
      if (tireWidthRank(spec.tireWidth) <= tireWidthRank(cls.maxTire)) {
        fits.push(`Your ${spec.tireWidth.toLowerCase()} tire is inside the class limit.`);
      } else {
        blockers.push(`Tires must be ${cls.maxTire.toLowerCase()}; yours are ${spec.tireWidth.toLowerCase()}.`);
      }
    }

    if (cls.bigTireOnly) {
      if (spec.tireWidth === 'Over 11.5 in — big tire') {
        fits.push('A big-tire class, and you are on a big tire.');
      } else {
        blockers.push('This is a big-tire class — tires must be wider than 11.5 in at the tread.');
      }
    }

    if (cls.engineFamilies) {
      if (cls.engineFamilies.includes(spec.engineFamily)) {
        fits.push(`${spec.engineFamily.split('—')[0]!.trim()} is the right half of the split.`);
      } else {
        blockers.push(`This class is ${cls.engineFamilies[0]!.toLowerCase()}; you have a ${spec.engineFamily.toLowerCase()}.`);
      }
    }

    if (cls.bodyYears) {
      if (spec.bodyYear === null) {
        blockers.push(`Bodies from ${cls.bodyYears[0]}–${cls.bodyYears[1]} only — enter your year to check it.`);
      } else {
        fits.push(`A ${spec.bodyYear} body is inside the ${cls.bodyYears[0]}–${cls.bodyYears[1]} window.`);
      }
    }

    // --- how it is built ---------------------------------------------------
    //
    // Three separate rules, because the rulebooks write three separate rules.
    // All of them are an exact fit rather than a ceiling: both drag-and-drive
    // rulebooks push a car DOWN into the tightest class it qualifies for
    // ("vehicles must compete in the lowest class for which they are eligible"),
    // so a stock-frame car is not free to enter Pro Street any more than a
    // back-halved one may enter Street Race. The mismatch reads both ways.
    if (cls.frame && cls.frame !== spec.frame) {
      blockers.push(`Written for a ${cls.frame.toLowerCase()}; yours is ${spec.frame.toLowerCase()}.`);
    } else if (cls.frame) {
      fits.push(`A ${cls.frame.toLowerCase()} is what the class is written for.`);
    }

    if (cls.stockFront) {
      if (spec.frontSuspension === 'Stock-type — factory pickup points') {
        // Said in the rulebook's own terms, because the reason racers get this
        // one wrong is assuming "aftermarket" and "not stock" are the same word.
        fits.push('Factory-type front suspension, which is all the class asks — aftermarket arms and coil-overs included.');
      } else {
        blockers.push('Needs the factory front geometry: no struts on a car that never had them, no straight axle.');
      }
    }

    if (cls.rear === 'stock' && isRearNonStock(spec.rearSuspension)) {
      blockers.push(`The rear suspension must keep the factory pickup points; yours is a ${spec.rearSuspension.toLowerCase()}.`);
    } else if (cls.rear === 'stock') {
      fits.push('Rear suspension in the factory pickup points, which is the class rule.');
    } else if (cls.rear === 'non-stock' && !isRearNonStock(spec.rearSuspension)) {
      // The requirement points the other way here: this is a class for cars that
      // have left stock behind, so a stock rear is what keeps you out of it.
      blockers.push('This class is written for a non-stock rear suspension — four-link, ladder bars or a solid-axle conversion.');
    } else if (cls.rear === 'non-stock') {
      fits.push(`A ${spec.rearSuspension.toLowerCase()} is the departure from stock this class is written for.`);
    }

    // --- tire construction -------------------------------------------------
    //
    // Radial or slick, which is not the same question as how wide. A blocker
    // rather than a gate: it is a set of tires, and the racer may well own both.
    if (cls.tires) {
      if (cls.tires.includes(spec.tire)) {
        fits.push(`A ${spec.tire.toLowerCase()} is an admitted tire for the class.`);
      } else {
        blockers.push(`Runs on ${cls.tires.map((t) => t.toLowerCase()).join(' or ')}; you are on a ${spec.tire.toLowerCase()}.`);
      }
    }

    if (cls.manualOnly) {
      if (spec.transmission === 'Manual (clutch)' || spec.transmission === 'Clutchless manual') {
        fits.push('A clutch car, which is the whole entry requirement.');
      } else {
        blockers.push(`Needs a pedal-operated clutch; you are running "${spec.transmission}".`);
      }
    }

    // --- ET caps -----------------------------------------------------------
    //
    // Not windows. These are the "run this number and you are out" rules, and
    // being on the wrong side of one is worth saying loudly — a Street Race car
    // that dips under 8.50 is disqualified, not reclassified.
    if (cls.maxQuicker !== undefined && et < cls.maxQuicker) {
      blockers.push(
        `A slip quicker than ${cls.maxQuicker.toFixed(2)} is a disqualification, and this pace is ${et.toFixed(2)}.`,
      );
    } else if (cls.maxQuicker !== undefined) {
      fits.push(`Comfortably on the legal side of the ${cls.maxQuicker.toFixed(2)} limit.`);
    }

    if (cls.minSlower !== undefined && et < cls.minSlower) {
      blockers.push(`Slips must be ${cls.minSlower.toFixed(2)} or slower; this pace is ${et.toFixed(2)}.`);
    } else if (cls.minSlower !== undefined) {
      fits.push(`At or slower than the ${cls.minSlower.toFixed(2)} floor.`);
    }

    // --- grade -------------------------------------------------------------
    const quality: MatchQuality = blockers.length > 0 ? 'possible' : inWindow ? 'strong' : 'likely';

    matches.push({ cls, quality, fits, blockers });
  }

  const rank: Record<MatchQuality, number> = { strong: 0, likely: 1, possible: 2 };
  return matches.sort((a, b) => {
    if (rank[a.quality] !== rank[b.quality]) return rank[a.quality] - rank[b.quality];
    // Then by how squarely the pace sits in the class — the middle of the window.
    const centre = (c: RaceClass) => Math.abs(et - (c.et[0] + c.et[1]) / 2);
    return centre(a.cls) - centre(b.cls);
  });
}

/**
 * How many blockers a class can carry and still be worth putting on the page.
 *
 * `matchClasses` deliberately reports everything it found, blockers and all —
 * that is the honest computation, and the tests check it. But everything is not
 * an answer. The drag-and-drive catalogues gate on almost nothing (you drive the
 * car, and that is most of it), so a real combination came back matching all
 * thirty-seven classes with a spread of blockers, which reads as noise rather
 * than as "here is your class".
 *
 * One blocker is a class you are one change away from — a cert tag, 200 lb of
 * ballast, a narrower tire. That is worth knowing and worth acting on. Two or
 * more is a different car.
 */
export const MAX_BLOCKERS = 1;

export interface Shortlist {
  shown: ClassMatch[];
  /** Classes the engine will not rank, because it has not really checked them. */
  unjudged: ClassMatch[];
  /**
   * The ones held back, in full rather than as a count.
   *
   * Reported rather than swallowed: a list that silently truncates reads as
   * "these are all your options", and a racer who cannot see that thirty were
   * dropped has no way to know to widen the search. The matches themselves are
   * kept because the interesting part is *why* each one is out — a tube chassis
   * and a four-link rule out most of a catalogue at once, and being told which
   * two facts did it is worth more than being told thirty classes are gone.
   *
   * Nearest first: fewest blockers at the top, so the list opens on the classes
   * that are two changes away rather than five.
   */
  held: ClassMatch[];
}

export function shortlist(matches: ClassMatch[]): Shortlist {
  const unjudged = matches.filter((m) => m.cls.unjudged);
  const judged = matches.filter((m) => !m.cls.unjudged);
  const shown = judged.filter((m) => m.blockers.length <= MAX_BLOCKERS);
  const held = judged
    .filter((m) => m.blockers.length > MAX_BLOCKERS)
    .sort((a, b) => a.blockers.length - b.blockers.length);
  return { shown, unjudged, held };
}

export const QUALITY_TEXT: Record<MatchQuality, string> = {
  strong: 'Strong match',
  likely: 'Likely',
  possible: 'Possible, with changes',
};
